import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight, Target, Activity, Eye, BarChart3 } from "lucide-react";
import SpeelruimteBanner from "@/components/SpeelruimteBanner";
import ProgramSwitcher from "@/components/ProgramSwitcher";
import MethodiekScroll from "@/components/MethodiekScroll";
import { SquiggleUnderline, RouteLine, FloatingBlob } from "@/components/Squiggle";
import Transitiemodel from "@/components/Transitiemodel";
import VoorschakelDiagram from "@/components/VoorschakelDiagram";
import EvidenceBronnen from "@/components/EvidenceBronnen";
import DownloadHub from "@/components/DownloadHub";
import { METHODIEK } from "@/data/methodiek";
import { BegrippenIntro, PijlersEnBegrippen, TwaalfWeken, Meetproces } from "@/components/MethodeOpbouw";
import { PROGRAMMAS } from "@/data/programmaLogic";
import { usePerspective } from "@/context/PerspectiveContext";
import usePageMeta from "@/hooks/usePageMeta";

const HERO_IMGS = [
  "https://images.unsplash.com/photo-1723227123897-0624fe046ae8?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  "https://images.unsplash.com/photo-1484627147104-f5197bcd6651?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
  "https://images.unsplash.com/photo-1522163182402-834f871fd851?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
  "https://images.unsplash.com/photo-1543269664-56d93c1b41a6?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
];

const COPY = {
  deelnemer: {
    eyebrow: "De methode achter LevelLeven",
    headline: (
      <>
        Harder duwen helpt niet
        <br />
        als je niet weet{" "}
        <span className="italic text-ll-coral relative inline-block">wat er remt<SquiggleUnderline color="#E06B3F" offset={-2} height={18} /></span>.
      </>
    ),
    belofte: "Speelruimte om te ontdekken wat wél werkt.",
    sub:
      "Zeven kernbegrippen die telkens terugkomen. Ze vormen samen één route — voor jou, voor je begeleider en voor wie erop kan bouwen.",
    programHint: "START duurt twaalf weken. Daarna kijk je samen wat past. Soms is dat meer tijd (VERDER), vaak iets anders.",
  },
  opdrachtgever: {
    eyebrow: "Methodiek & onderbouwing",
    headline: (
      <>
        Harder duwen helpt niet.{" "}
        <span className="italic text-ll-coral relative inline-block">Ontdekken wat remt, wél<SquiggleUnderline color="#E06B3F" offset={-2} height={18} /></span>.
      </>
    ),
    belofte: "De volgende beweging begint klein.",
    sub:
      "LevelLeven heeft één eigen methodiek: zes pijlers voor het ontwerp, zeven kernbegrippen voor de beleving. We gebruiken inzichten uit bestaande benaderingen als gereedschap. De effectiviteit van LevelLeven zelf wordt nog onderzocht.",
    programHint: "LevelLeven START is het gemeentelijke programma van 12 weken. VERDER is een mogelijk vervolg, geen automatische voortzetting; de specificaties volgen nog.",
  },
};

export default function Methode() {
  const { perspective } = usePerspective();
  const [programma, setProgramma] = useState("start");
  const [openBegrip, setOpenBegrip] = useState(null);
  const p = PROGRAMMAS[programma];
  const c = COPY[perspective] || COPY.deelnemer;

  usePageMeta({
    title: "De methode · LevelLeven",
    description:
      "De LevelLeven-methode in zeven kernbegrippen: Startpunt, Rugzak, Rem, Vonk, Speelruimte, LevelMoment en Volgende beweging.",
  });

  return (
    <div className="bg-ll-cream text-ll-ink">
      {/* HERO — perspectief-aware */}
      <AnimatePresence mode="wait">
        <motion.div
          key={perspective}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
        >
          <SpeelruimteBanner
            eyebrow={c.eyebrow}
            headline={c.headline}
            belofte={c.belofte}
            subhead={c.sub}
            images={HERO_IMGS}
            tone={perspective === "opdrachtgever" ? "sky" : "coral"}
            ctaPrimary={{ label: "Naar de zeven begrippen", to: "#begrippen" }}
          />
        </motion.div>
      </AnimatePresence>

      {/* PROGRAMMA SWITCHER */}
      <section className="py-20 relative">
        <FloatingBlob color="#F0B98D" size={120} className="top-10 right-10 opacity-70" />
        <div className="max-w-[1200px] mx-auto px-6 sm:px-10 lg:px-16">
          <div className="text-center max-w-3xl mx-auto">
            <p className="text-[10px] uppercase tracking-[0.35em] text-ll-coral">Twee intensiteiten · één methode</p>
            <h2 className="mt-6 font-heading text-3xl sm:text-4xl lg:text-5xl leading-tight font-light">
              START staat op zichzelf.{" "}
              <span className="italic text-ll-coral">VERDER is een mogelijk vervolg.</span>
            </h2>
            <p className="mt-6 text-ll-navy/80 leading-relaxed">{c.programHint}</p>
            <div className="mt-10 flex justify-center">
              <ProgramSwitcher value={programma} onChange={setProgramma} size="lg" />
            </div>
          </div>

          <AnimatePresence mode="wait">
            <motion.div
              key={programma}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.45 }}
              className="mt-16 grid grid-cols-1 lg:grid-cols-12 gap-10 items-start"
              data-testid={`programma-panel-${programma}`}
            >
              <div className="lg:col-span-5">
                <p className="chapter-num text-6xl" style={{ color: p.accent }}>{p.label}</p>
                <p className="mt-2 text-xs uppercase tracking-[0.3em] text-ll-navy/60">{p.ondertitel}</p>
                <h3 className="mt-6 font-heading text-3xl sm:text-4xl font-light leading-tight">
                  {p.korteOmschrijving}
                </h3>
                <p className="mt-6 text-ll-navy/80 leading-relaxed">
                  {p.voor[perspective] || p.voor.deelnemer}
                </p>
                <dl className="mt-8 space-y-3 text-sm">
                  <div className="flex justify-between border-b border-ll-ink/10 pb-2"><dt className="text-ll-navy/60 uppercase tracking-[0.2em] text-[10px]">Duur</dt><dd>{p.duur}</dd></div>
                  <div className="flex justify-between border-b border-ll-ink/10 pb-2"><dt className="text-ll-navy/60 uppercase tracking-[0.2em] text-[10px]">Intensiteit</dt><dd>{p.intensiteit}</dd></div>
                  <div className="flex justify-between border-b border-ll-ink/10 pb-2"><dt className="text-ll-navy/60 uppercase tracking-[0.2em] text-[10px]">Groep</dt><dd>{p.groep}</dd></div>
                  <div className="flex justify-between border-b border-ll-ink/10 pb-2"><dt className="text-ll-navy/60 uppercase tracking-[0.2em] text-[10px]">Kosten</dt><dd>{p.kosten}</dd></div>
                </dl>
              </div>
              <div className="lg:col-span-7">
                <p className="text-[10px] uppercase tracking-[0.3em] text-ll-coral mb-6">Fasering</p>
                <ol className="space-y-4">
                  {p.fases.map((f, i) => (
                    <motion.li
                      key={f.n}
                      initial={{ opacity: 0, x: -12 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: i * 0.08, duration: 0.4 }}
                      className="flex gap-6 p-6 rounded-2xl bg-white border border-ll-ink/10"
                    >
                      <div className="shrink-0 chapter-num text-2xl" style={{ color: p.accent }}>{f.n}</div>
                      <div>
                        <p className="font-heading text-xl text-ll-ink">{f.t}</p>
                        <p className="mt-2 text-sm text-ll-navy/80">{f.d}</p>
                      </div>
                    </motion.li>
                  ))}
                </ol>
                <p className="mt-6 text-sm italic text-ll-navy/70">
                  Resultaat: {p.resultaat[perspective] || p.resultaat.deelnemer}
                </p>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </section>

      {/* ZEVEN BEGRIPPEN — interactieve scroll-lijn */}
      <BegrippenIntro />
      <MethodiekScroll />

      {/* PIJLERS ↔ BEGRIPPEN, TWAALF WEKEN, MEETPROCES */}
      <PijlersEnBegrippen />
      <TwaalfWeken />
      <Meetproces />

      {/* VOORSCHAKEL POSITIONERING (opdrachtgever-focused) */}
      {perspective === "opdrachtgever" && (
        <section className="py-24 sm:py-32">
          <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
            <div className="max-w-3xl mb-14">
              <p className="text-[10px] uppercase tracking-[0.35em] text-ll-coral">Waar wij zitten in de keten</p>
              <h2 className="mt-6 font-heading text-4xl sm:text-5xl leading-tight tracking-tight font-light">
                Een nieuwe categorie:{" "}
                <span className="italic text-ll-coral">de voorschakel-interventie</span>.
              </h2>
            </div>
            <VoorschakelDiagram />
          </div>
        </section>
      )}

      {/* TRANSITIEMODEL (only opdrachtgever) */}
      {perspective === "opdrachtgever" && (
        <section className="py-24 sm:py-32 bg-[#F0EAD8]">
          <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
            <div className="max-w-3xl mb-14">
              <p className="text-[10px] uppercase tracking-[0.35em] text-ll-coral">Het model</p>
              <h2 className="mt-6 font-heading text-4xl sm:text-5xl lg:text-6xl leading-tight tracking-tight font-light">
                Het bredere ontwikkelmodel,{" "}
                <span className="italic text-ll-coral">in negen fasen</span>.
              </h2>
              <p className="mt-6 text-ll-navy/80 leading-relaxed">
                Een achtergrondmodel dat verder kijkt dan START. De twaalf weken van START vormen het begin; latere fasen lopen via bestaande voorzieningen of een mogelijk vervolg.
              </p>
            </div>
            <Transitiemodel />
          </div>
        </section>
      )}

      {/* EVIDENCE (opdrachtgever) / OPROEP (deelnemer) */}
      {perspective === "opdrachtgever" ? (
        <section className="py-24 sm:py-32">
          <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
            <EvidenceBronnen />
            <div className="mt-16">
              <DownloadHub />
            </div>
          </div>
        </section>
      ) : (
        <section className="py-24 sm:py-32">
          <div className="max-w-[1000px] mx-auto px-6 sm:px-10 lg:px-16 text-center">
            <p className="text-[10px] uppercase tracking-[0.35em] text-ll-coral">Wat vraag jij jezelf af?</p>
            <h2 className="mt-6 font-heading text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tight font-light">
              Begin niet met een doel.{" "}
              <span className="italic text-ll-coral">Begin met een vraag.</span>
            </h2>
            <p className="mt-6 text-lg text-ll-navy/80 leading-relaxed max-w-xl mx-auto">
              Deze zeven vragen zijn geen test. Ze zijn een uitnodiging om weer bij jezelf naar binnen te kijken. Kom een keer meelopen en ontdek welke woord bij jou past.
            </p>
            <div className="mt-10">
              <Link
                to="/voor-jou"
                data-testid="methode-slot-cta-deelnemer"
                className="group inline-flex items-center gap-4 bg-ll-ink text-ll-cream hover:bg-ll-coral rounded-full pl-7 pr-2 py-2 transition-all duration-300"
              >
                <span className="text-base font-medium">Ontdek wat er voor jou is</span>
                <span className="bg-ll-coral group-hover:bg-ll-yellow text-ll-ink w-11 h-11 rounded-full flex items-center justify-center transition-colors">
                  <ArrowUpRight size={18} />
                </span>
              </Link>
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
